import { Alert, Col, Row, Spacer } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { IPatientContactInfoDraft } from '../../../common/validation/patientContactInfo';
import type { ICustomControlRequestAddEditProps } from '../pages/customControls/interfaces/';
import { ServiceCode } from '../../../common/enums/serviceCode';
import { translateKey } from '../../translate';
import { useEpisodeContext } from '../../utils/hooks/useEpisodeContext';
import { CheckboxField } from '../fields/Checkbox';
import { FormDivider, HeaderDivider, InputTextArea } from './common';
import { FormLabel } from './forms/FormLabel';
import { HorizontalFormGroup } from './forms/HorizontalFormGroup';
import { StackedInputs } from './forms/StackedInputs';

interface IPatientContactAddEditProps {
    addressMandatory?: boolean;
    alertMessage?: string;
    hideHeader?: boolean;
    showAddress?: boolean;
    showEmailOnly?: boolean;
    useDeliveryAddress?: boolean;
}

export function PatientContactAddEdit(props: IPatientContactAddEditProps & ICustomControlRequestAddEditProps) {
    const { patient } = useEpisodeContext();
    const [contactInfoAlreadyKnown, setContactInfoAlreadyKnown] = useState(false);
    const [isSendToDeliveryAddress, setIsSendToDeliveryAddress] = useState(
        props.request.customControlData?.sendToDeliveryAddress || false
    );
    const contactData = props.request.customControlData.contactgegevens_patient || {};
    const deliveryAddress = props.request.customControlData.afleveradres_patient || {};

    useEffect(() => {
        const update: {
            city?: string;
            country?: string;
            emailadres?: string;
            homePhoneNumber?: string;
            houseNumber?: string;
            houseNumberSuffix?: string;
            mobileNumber?: string;
            street?: string;
            zip?: string;
        } = {};

        if (patient.emailAddress && !contactData.emailadres) {
            update.emailadres = patient.emailAddress;
        }

        if (!props.showEmailOnly) {
            if (patient.homePhoneNumber && !contactData.homePhoneNumber) {
                update.homePhoneNumber = patient.homePhoneNumber;
            }

            if (patient.mobileNumber && !contactData.mobileNumber) {
                update.mobileNumber = patient.mobileNumber;
            }
        }

        if (props.showAddress) {
            if (patient.street && !contactData.street) {
                update.street = patient.street;
            }

            if (patient.zip && !contactData.zip) {
                update.zip = patient.zip;
            }

            if (patient.city && !contactData.city) {
                update.city = patient.city;
            }

            if (patient.houseNumber && !contactData.houseNumber) {
                update.houseNumber = patient.houseNumber;
            }

            if (patient.houseNumberSuffix && !contactData.houseNumberSuffix) {
                update.houseNumberSuffix = patient.houseNumberSuffix;
            }

            update.country = 'Nederland';
        }

        if (Object.values(update).some((patientUpdate) => patientUpdate !== undefined)) {
            setContactInfoAlreadyKnown(true);
            props.handleChangeCustomControl({
                contactgegevens_patient: {
                    ...props.request.customControlData.contactgegevens_patient,
                    ...update,
                },
            });
        }
    }, [props.showAddress]);

    useEffect(() => {
        if (isSendToDeliveryAddress) {
            props.handleChangeCustomControl({
                sendToDeliveryAddress: true,
                afleveradres_patient: {
                    ...props.request.customControlData.afleveradres_patient,
                },
            });
        } else {
            props.handleChangeCustomControl({
                sendToDeliveryAddress: false,
                afleveradres_patient: {
                    street: null,
                    zip: null,
                    city: null,
                    houseNumber: null,
                    houseNumberSuffix: null,
                },
            });
        }
    }, [isSendToDeliveryAddress]);

    return (
        <>
            {!props.hideHeader && <HeaderDivider text={translateKey('common.contactPatient')} />}

            {contactInfoAlreadyKnown && !props.alertMessage && (
                <Alert appearance="warning">{translateKey('common.checkContactInfo')}</Alert>
            )}

            {props.alertMessage && <Alert appearance="warning">{props.alertMessage}</Alert>}

            {props.showAddress && (
                <>
                    <HorizontalFormGroup
                        hasErrors={
                            props.errors.has('customControlData.contactgegevens_patient.street') ||
                            props.errors.has('customControlData.contactgegevens_patient.houseNumber') ||
                            props.errors.has('customControlData.contactgegevens_patient.houseNumberSuffix') ||
                            props.errors.has('customControlData.contactgegevens_patient.zip') ||
                            props.errors.has('customControlData.contactgegevens_patient.city') ||
                            props.errors.has('customControlData.contactgegevens_patient.country')
                        }
                    >
                        <FormLabel isRequired={props.addressMandatory}>{translateKey('patient.address')}</FormLabel>
                        <StackedInputs>
                            <Spacer spacing="small">
                                <Row
                                    columnGap={[2, 4]}
                                    rowGap={[2, 4]}
                                >
                                    <Col span={[12, 6]}>
                                        <InputTextArea
                                            automation="street"
                                            error={props.errors.get('customControlData.contactgegevens_patient.street')}
                                            onChange={async (value) => await handleChange('street', value)}
                                            onDoValidate={async () =>
                                                await props.validateOneCustomControl('contactgegevens_patient.street')
                                            }
                                            placeholder={translateKey('common.street')}
                                            value={contactData.street}
                                        />
                                    </Col>
                                    <Col span={[12, 3]}>
                                        <InputTextArea
                                            automation="houseNumber"
                                            error={props.errors.get(
                                                'customControlData.contactgegevens_patient.houseNumber'
                                            )}
                                            onChange={async (value) => await handleChange('houseNumber', value)}
                                            onDoValidate={async () =>
                                                await props.validateOneCustomControl(
                                                    'contactgegevens_patient.houseNumber'
                                                )
                                            }
                                            placeholder={translateKey('common.numberShort')}
                                            value={contactData.houseNumber}
                                        />
                                    </Col>
                                    <Col span={[12, 3]}>
                                        <InputTextArea
                                            automation="houseNumberSuffix"
                                            error={props.errors.get(
                                                'customControlData.contactgegevens_patient.houseNumberSuffix'
                                            )}
                                            onChange={async (value) => await handleChange('houseNumberSuffix', value)}
                                            onDoValidate={async () =>
                                                await props.validateOneCustomControl(
                                                    'contactgegevens_patient.houseNumberSuffix'
                                                )
                                            }
                                            placeholder={translateKey('patient.houseNumberSuffixShort')}
                                            value={contactData.houseNumberSuffix}
                                        />
                                    </Col>
                                </Row>
                                <Row
                                    columnGap={[2, 4]}
                                    rowGap={[2, 4]}
                                >
                                    <Col span={[12, 4]}>
                                        <InputTextArea
                                            automation="zip"
                                            error={props.errors.get('customControlData.contactgegevens_patient.zip')}
                                            onChange={async (value) => await handleChange('zip', value)}
                                            onDoValidate={async () =>
                                                await props.validateOneCustomControl('contactgegevens_patient.zip')
                                            }
                                            placeholder={translateKey('patient.zip')}
                                            value={contactData.zip}
                                        />
                                    </Col>
                                    <Col span={[12, 8]}>
                                        <InputTextArea
                                            automation="city"
                                            error={props.errors.get('customControlData.contactgegevens_patient.city')}
                                            onChange={async (value) => await handleChange('city', value)}
                                            onDoValidate={async () =>
                                                await props.validateOneCustomControl('contactgegevens_patient.city')
                                            }
                                            placeholder={translateKey('common.city')}
                                            value={contactData.city}
                                        />
                                    </Col>
                                </Row>
                                <Row>
                                    <Col span={12}>
                                        <InputTextArea
                                            automation="country"
                                            disabled
                                            error={props.errors.get(
                                                'customControlData.contactgegevens_patient.country'
                                            )}
                                            onChange={async (value) => await handleChange('country', value)}
                                            onDoValidate={async () =>
                                                await props.validateOneCustomControl('contactgegevens_patient.country')
                                            }
                                            placeholder={translateKey('common.country')}
                                            value={contactData.country}
                                        />
                                    </Col>
                                </Row>
                            </Spacer>
                        </StackedInputs>
                    </HorizontalFormGroup>
                </>
            )}

            <HorizontalFormGroup hasErrors={props.errors.has('customControlData.contactgegevens_patient.emailadres')}>
                <FormLabel isRequired={props.service.code !== ServiceCode.GGZCf2f}>
                    {getEmailAddressLabel(props.service.code)}
                </FormLabel>
                <StackedInputs>
                    <Row>
                        <Col span={12}>
                            <InputTextArea
                                automation="emailAddress"
                                error={props.errors.get('customControlData.contactgegevens_patient.emailadres')}
                                onChange={async (value) => await handleChange('emailadres', value)}
                                onDoValidate={async () =>
                                    await props.validateOneCustomControl('contactgegevens_patient.emailadres')
                                }
                                placeholder="E-mailadres voor het maken van een afspraak..."
                                value={contactData.emailadres}
                            />
                        </Col>
                    </Row>
                </StackedInputs>
            </HorizontalFormGroup>

            {!props.showEmailOnly && (
                <>
                    <HorizontalFormGroup
                        hasErrors={
                            props.errors.has('customControlData.contactgegevens_patient.mobileNumber') ||
                            props.errors.has('customControlData.contactgegevens_patient.homePhoneNumber')
                        }
                    >
                        <FormLabel isRequired>{getTelephoneNumberLabel(props.service.code)}</FormLabel>
                        <StackedInputs>
                            <Row
                                columnGap={[2, 4]}
                                rowGap={[2, 4]}
                            >
                                <Col span={[12, 6]}>
                                    <InputTextArea
                                        automation="mobileNumber"
                                        error={getTelephoneNumberError(
                                            'customControlData.contactgegevens_patient.mobileNumber'
                                        )}
                                        onChange={async (value) => await handleChange('mobileNumber', value)}
                                        onDoValidate={async () =>
                                            await props.validateOneCustomControl('contactgegevens_patient.mobileNumber')
                                        }
                                        placeholder={translateKey('common.mobileNumber')}
                                        value={contactData.mobileNumber}
                                    />
                                </Col>
                                <Col span={[12, 6]}>
                                    <InputTextArea
                                        automation="homeNumber"
                                        error={getTelephoneNumberError(
                                            'customControlData.contactgegevens_patient.homePhoneNumber'
                                        )}
                                        onChange={async (value) => await handleChange('homePhoneNumber', value)}
                                        onDoValidate={async () => {
                                            await props.validateOneCustomControl(
                                                'contactgegevens_patient.homePhoneNumber'
                                            );
                                            await props.validateOneCustomControl(
                                                'contactgegevens_patient.mobileNumber'
                                            );
                                        }}
                                        placeholder={translateKey('common.homeNumber')}
                                        value={contactData.homePhoneNumber}
                                    />
                                </Col>
                            </Row>
                        </StackedInputs>
                    </HorizontalFormGroup>
                </>
            )}

            {props.showAddress && props.useDeliveryAddress && (
                <>
                    <FormDivider />
                    <CheckboxField
                        checked={isSendToDeliveryAddress}
                        className="mb-0"
                        label=""
                        onChange={() => setIsSendToDeliveryAddress(!isSendToDeliveryAddress)}
                        text="Thuisholter afleveren op ander adres"
                    />
                </>
            )}

            {props.showAddress && props.useDeliveryAddress && isSendToDeliveryAddress && (
                <>
                    <HorizontalFormGroup
                        hasErrors={
                            props.errors.has('customControlData.afleveradres_patient.street') ||
                            props.errors.has('customControlData.afleveradres_patient.houseNumber') ||
                            props.errors.has('customControlData.afleveradres_patient.houseNumberSuffix') ||
                            props.errors.has('customControlData.afleveradres_patient.zip') ||
                            props.errors.has('customControlData.afleveradres_patient.city') ||
                            props.errors.has('customControlData.afleveradres_patient.country')
                        }
                    >
                        <FormLabel isRequired={isSendToDeliveryAddress}>
                            {translateKey('patient.deliveryAddress')}
                        </FormLabel>
                        <StackedInputs>
                            <Spacer spacing="small">
                                <Row
                                    columnGap={[2, 4]}
                                    rowGap={[2, 4]}
                                >
                                    <Col span={[12, 6]}>
                                        <InputTextArea
                                            automation="street"
                                            error={props.errors.get('customControlData.afleveradres_patient.street')}
                                            onChange={async (value) => await handleChangeDelivery('street', value)}
                                            onDoValidate={async () =>
                                                await props.validateOneCustomControl('afleveradres_patient.street')
                                            }
                                            placeholder={translateKey('common.street')}
                                            value={deliveryAddress.street}
                                        />
                                    </Col>
                                    <Col span={[12, 3]}>
                                        <InputTextArea
                                            automation="houseNumber"
                                            error={props.errors.get(
                                                'customControlData.afleveradres_patient.houseNumber'
                                            )}
                                            onChange={async (value) => await handleChangeDelivery('houseNumber', value)}
                                            onDoValidate={async () =>
                                                await props.validateOneCustomControl('afleveradres_patient.houseNumber')
                                            }
                                            placeholder={translateKey('common.numberShort')}
                                            value={deliveryAddress.houseNumber}
                                        />
                                    </Col>
                                    <Col span={[12, 3]}>
                                        <InputTextArea
                                            automation="houseNumberSuffix"
                                            error={props.errors.get(
                                                'customControlData.afleveradres_patient.houseNumberSuffix'
                                            )}
                                            onChange={async (value) =>
                                                await handleChangeDelivery('houseNumberSuffix', value)
                                            }
                                            onDoValidate={async () =>
                                                await props.validateOneCustomControl(
                                                    'afleveradres_patient.houseNumberSuffix'
                                                )
                                            }
                                            placeholder={translateKey('patient.houseNumberSuffixShort')}
                                            value={deliveryAddress.houseNumberSuffix}
                                        />
                                    </Col>
                                </Row>
                                <Row
                                    columnGap={[2, 4]}
                                    rowGap={[2, 4]}
                                >
                                    <Col span={[12, 4]}>
                                        <InputTextArea
                                            automation="zip"
                                            error={props.errors.get('customControlData.afleveradres_patient.zip')}
                                            onChange={async (value) => await handleChangeDelivery('zip', value)}
                                            onDoValidate={async () =>
                                                await props.validateOneCustomControl('afleveradres_patient.zip')
                                            }
                                            placeholder={translateKey('patient.zip')}
                                            value={deliveryAddress.zip}
                                        />
                                    </Col>
                                    <Col span={[12, 8]}>
                                        <InputTextArea
                                            automation="city"
                                            error={props.errors.get('customControlData.afleveradres_patient.city')}
                                            onChange={async (value) => await handleChangeDelivery('city', value)}
                                            onDoValidate={async () =>
                                                await props.validateOneCustomControl('afleveradres_patient.city')
                                            }
                                            placeholder={translateKey('common.city')}
                                            value={deliveryAddress.city}
                                        />
                                    </Col>
                                </Row>
                            </Spacer>
                        </StackedInputs>
                    </HorizontalFormGroup>
                </>
            )}
        </>
    );

    async function handleChange<K extends keyof IPatientContactInfoDraft>(name: K, value: IPatientContactInfoDraft[K]) {
        await props.handleChangeCustomControl({
            contactgegevens_patient: {
                ...props.request.customControlData.contactgegevens_patient,
                [name]: value,
            },
        });
    }

    async function handleChangeDelivery<K extends keyof IPatientContactInfoDraft>(
        name: K,
        value: IPatientContactInfoDraft[K]
    ) {
        await props.handleChangeCustomControl({
            afleveradres_patient: {
                ...props.request.customControlData.afleveradres_patient,
                [name]: value,
            },
        });
    }

    function getEmailAddressLabel(serviceCode: ServiceCode) {
        if (serviceCode === ServiceCode.GGZCf2f) {
            return translateKey('patient.patientEmailAddress');
        } else if (serviceCode === ServiceCode.DermaOth) {
            return translateKey('common.emailForInvitation');
        }

        return translateKey('common.emailAddress');
    }

    function getTelephoneNumberError(fieldKey: string) {
        const error = props.errors.get(fieldKey);
        if (error === 'string_base') {
            return 'onePhoneNumberRequired';
        } else if (error === 'string_pattern.base') {
            return 'invalidPhoneNumber';
        }

        return error;
    }

    function getTelephoneNumberLabel(serviceCode: ServiceCode) {
        if (serviceCode === ServiceCode.GGZCf2f) {
            return translateKey('patient.patientPhoneNumber');
        }

        return translateKey('common.phoneNumber');
    }
}
